/**
 * Platform module catalog.
 *
 * One source of truth for the platform surface, so a module is added or flipped
 * to live in exactly one place.
 *
 * Note the division of labour with `shared/nav.product.ts`: nav decides what
 * appears in the signed-in sidebar and only lists modules that are actually
 * built. This catalog describes the whole surface, including what is planned,
 * because the pricing page has to be able to say which modules a plan includes
 * without promising more than the product admits to.
 */

import {
  Globe, ShieldCheck, Server, Database, Zap, HardDrive,
  Mail, Clock, Rocket, Activity, ScrollText, Code2, CreditCard,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type ModuleStatus = 'live' | 'planned';

export interface PlatformModule {
  key: string;
  label: string;
  href: string;
  icon: LucideIcon;
  /** One line for the dashboard card. */
  summary: string;
  /** What the module will do, shown on the placeholder page. */
  detail: string;
  /** Concrete capabilities, so a half-built module is never mistaken for done. */
  scope: string[];
  status: ModuleStatus;
  /**
   * Lives inside each app's deployment page instead of the sidebar. Its page
   * stays reachable from there, for the parts that are server-wide.
   */
  inProject?: boolean;
  /** The sidebar's name for it, when that differs from the catalog label. */
  navLabel?: string;
  /** Sidebar position, when it should not follow catalog order. Customers is 50. */
  navOrder?: number;
  /** Kept off the sidebar entirely; reached through another item's page. */
  hideInNav?: boolean;
  /**
   * Kept out of the marketing "what is in the bundle" grid.
   *
   * For the modules that are how bloomneo is *run* rather than what it
   * operates. Billing is the arrangement around the service, not a thing we
   * manage on the customer's behalf — listing it beside servers and
   * certificates offers an invoice as a feature.
   */
  hideInBundle?: boolean;
  /** Other routes that light this item up in the sidebar. */
  navAlso?: string[];
}

/**
 * Every module is offered today. `status` is retained so a future module can be
 * staged before launch without touching the pages that read this catalog.
 */
export function moduleAccent(module: PlatformModule): string {
  return module.status === 'live'
    ? 'bg-primary/10 text-primary'
    : 'bg-muted text-muted-foreground';
}

/** Working modules first — the dashboard should lead with what's usable. */
export function sortedModules(): PlatformModule[] {
  return [...PLATFORM_MODULES].sort((a, b) =>
    (a.status === 'live' ? 0 : 1) - (b.status === 'live' ? 0 : 1)
  );
}

export const PLATFORM_MODULES: PlatformModule[] = [
  {
    key: 'dns',
    navLabel: 'Domains & SSL',
    navAlso: ['/ssl'],
    label: 'DNS Management',
    href: '/dns',
    icon: Globe,
    summary: 'Manage DNS zones and records',
    detail: 'DNS zones mirrored from DigitalOcean, each assignable to a customer who manages only their own records.',
    scope: ['Zone sync from DigitalOcean', 'Per-customer zone ownership', 'A / AAAA / CNAME / MX / TXT / SRV / CAA records'],
    status: 'live',
  },
  {
    key: 'ssl',
    hideInNav: true,
    label: 'SSL Certificates',
    href: '/ssl',
    icon: ShieldCheck,
    summary: "Let's Encrypt issuance and renewal",
    detail: 'Certificates issued through certbot on the Bloom Projects server for domains with active DNS.',
    scope: ['Issue and revoke certificates', 'Per-domain SSL status', 'Renewal tracking'],
    status: 'live',
  },
  {
    key: 'servers',
    navLabel: 'Servers',
    label: 'Server Management',
    href: '/servers',
    icon: Server,
    summary: 'Droplets, provisioning and health',
    detail: 'A managed view over the DigitalOcean droplets that run customer workloads.',
    scope: ['Droplet inventory and sizing', 'Provision and decommission', 'Nginx site configuration', 'Resource and load metrics'],
    status: 'live',
  },
  {
    key: 'database',
    // Server-wide like Servers, so it sits beside it; each app's own database stays on its Data tab.
    navLabel: 'DB & Backups',
    label: 'Database & Backups',
    href: '/database',
    icon: Database,
    summary: 'Postgres instances and restore points',
    detail: 'Provisioning, monitoring, and scheduled backups for the Postgres databases behind customer applications.',
    scope: ['Instance provisioning', 'Scheduled backups with retention', 'Point-in-time restore', 'Connection and storage metrics'],
    status: 'live',
  },
  {
    key: 'cache',
    navLabel: 'Cache',
    inProject: true,
    label: 'Cache Management',
    href: '/cache',
    icon: Zap,
    summary: 'Redis instances, keys and eviction',
    detail: 'Cache instances attached to customer applications, with visibility into keyspace and hit rates.',
    scope: ['Instance provisioning', 'Keyspace and memory usage', 'Targeted and full flush', 'Hit-rate and eviction metrics'],
    status: 'live',
  },
  {
    key: 'storage',
    navLabel: 'Storage',
    label: 'Storage',
    href: '/storage',
    icon: HardDrive,
    summary: 'Wasabi buckets and access keys',
    detail: 'Object storage on Wasabi, with a bucket and access policy per customer.',
    scope: ['Bucket provisioning per customer', 'Access key lifecycle', 'Usage and egress reporting', 'Lifecycle and retention rules'],
    status: 'live',
  },
  {
    key: 'email',
    navLabel: 'Email',
    label: 'Transactional Email',
    href: '/email',
    icon: Mail,
    summary: 'Templates, delivery and bounces',
    detail: 'Transactional sending over the configured SMTP provider, with per-customer templates and delivery history.',
    scope: ['Template management', 'Delivery, bounce and complaint logs', 'Per-customer sending domains', 'Retry and suppression handling'],
    status: 'live',
  },
  {
    key: 'billing',
    hideInBundle: true,
    navLabel: 'Billing',
    label: 'Billing',
    href: '/billing',
    icon: CreditCard,
    /*
       Last in the sidebar, directly above Settings (order 130). Money is not
       one of the things being operated - it is the arrangement around them -
       so it reads at the bottom with the account, rather than in the middle of
       servers, databases and storage. Explicit rather than catalog order, the
       way App deploy pins itself to 55.
    */
    navOrder: 125,
    summary: 'Subscriptions and the events behind them',
    detail: 'Creem is the merchant of record internationally and Razorpay settles in rupees for India. This reads what they have told us: who is subscribed, on what, until when, and whether every webhook they sent was understood.',
    scope: [
      'Subscriptions per customer, as the gateway last reported them',
      'Every webhook received, and whether it was applied',
      'Resync one subscription when an event was missed',
    ],
    status: 'live',
  },
  {
    key: 'schedulers',
    inProject: true,
    label: 'Schedulers',
    href: '/schedulers',
    icon: Clock,
    summary: 'Cron jobs and run history',
    detail: 'Scheduled jobs that run against customer applications, with history and failure alerting.',
    scope: ['Cron definition and cadence', 'Run history with output', 'Failure alerts', 'Manual trigger and pause'],
    status: 'live',
  },
  {
    key: 'codebase',
    inProject: true,
    label: 'Codebase',
    href: '/codebase',
    icon: Code2,
    summary: 'Source delivery without repo access',
    detail: 'A customer\'s repository is linked here, and they download the source as a ZIP. They never receive access to the GitHub repository itself.',
    scope: [
      'Link a GitHub repository to a customer',
      'Download a branch or tag as a ZIP, proxied server-side',
      'No collaborator access granted on the repository',
      'Record of who downloaded which revision, and when',
    ],
    status: 'live',
  },
  {
    key: 'deployments',
    navLabel: 'App deploy',
    navOrder: 55,
    label: 'Deployment',
    href: '/deployments',
    icon: Rocket,
    summary: 'Releases, rollouts and rollback',
    detail: 'Application releases onto managed servers, with a record of what shipped and the ability to roll back.',
    scope: ['Release pipeline and history', 'Environment configuration', 'One-click rollback', 'Build and deploy logs'],
    status: 'live',
  },
  {
    key: 'logs',
    inProject: true,
    label: 'Server Logs',
    href: '/logs',
    icon: ScrollText,
    summary: 'Application, web server and process logs',
    detail:
      'Searchable access to what the managed server is writing: appkit application logs, ' +
      'nginx access and error logs, pm2 stdout and stderr, and system logs. Distinct from ' +
      'the audit trail under Admin, which records who did what in this platform.',
    scope: [
      'appkit JSON application logs, one file per day per app',
      'nginx access and error logs per site',
      'pm2 stdout and stderr, and the pm2 daemon log',
      'Level and full-text filtering, run on the server',
      'Error trend over the last week or month',
    ],
    status: 'live',
  },
  {
    key: 'uptime',
    inProject: true,
    label: 'Uptime Monitoring',
    href: '/uptime',
    icon: Activity,
    summary: 'Checks, incidents and alerting',
    detail: 'Availability checks against customer endpoints, with incident history and alerting.',
    scope: ['HTTP and TCP checks', 'Incident timeline', 'Alert routing', 'Response-time and uptime reporting'],
    status: 'live',
  },
];

export function findModule(key: string): PlatformModule | undefined {
  return PLATFORM_MODULES.find((module) => module.key === key);
}
